import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import PublishIcon from '@mui/icons-material/Publish'
import RestoreIcon from '@mui/icons-material/Restore'
import {
  useGetPublicationChangesQuery,
  useGetPublicationOperationQuery,
  useListPublicationsQuery,
  usePromotePublicationMutation,
  usePublishWebsiteMutation,
  type PublicationOperation,
  type PublicationSummary,
  type Manifest,
} from '../../api/authoring'
import { problemMessage } from '../../api/baseApi'
import { ComponentName } from '../../ui/ComponentName'
import { Loading, QueryError } from '../../ui/QueryState'
import { ReleaseContents } from './ReleaseContents'
import { shown } from '../../api/useIntent'

/** The release history and the two Website-level release actions. */
export function Releases({ tenant, site, manifest }: { tenant: string; site: string; manifest: Manifest | undefined }) {
  const [activeOperation, setActiveOperation] = useState<string>()
  const [lastOperation, setLastOperation] = useState<PublicationOperation>()
  const [awaitingPublication, setAwaitingPublication] = useState<number>()
  const [awaitingCurrent, setAwaitingCurrent] = useState<number>()
  const [rollback, setRollback] = useState<PublicationSummary>()
  const [opened, setOpened] = useState<number>()

  const list = useListPublicationsQuery(
    { tenant, website: site },
    {
      pollingInterval: activeOperation || awaitingPublication || awaitingCurrent ? 750 : 0,
      refetchOnMountOrArgChange: true,
    },
  )
  const changes = useGetPublicationChangesQuery(
    { tenant, website: site },
    { refetchOnMountOrArgChange: true },
  )
  const refetchChanges = changes.refetch
  const operation = useGetPublicationOperationQuery(
    { tenant, operationId: activeOperation ?? '' },
    { skip: !activeOperation, pollingInterval: activeOperation ? 750 : 0 },
  )
  const [publish, publishing] = usePublishWebsiteMutation()
  const [promote, promoting] = usePromotePublicationMutation()

  // Only an answer about the publish in progress is read. RTK Query keeps
  // a query's last `data` when its argument changes or it is skipped, so
  // an earlier publish's outcome would otherwise settle — and explain — a
  // later one before that one's own first poll had answered.
  const polled = activeOperation && operation.currentData?.id === activeOperation ? operation.currentData : undefined

  useEffect(() => {
    const settled = polled
    if (!settled || (settled.status !== 'published' && settled.status !== 'failed')) return
    setLastOperation(settled)
    setActiveOperation(undefined)
    if (settled.status === 'published' && settled.publication) setAwaitingPublication(settled.publication)
  }, [polled])

  useEffect(() => {
    if (awaitingPublication && list.data?.publications.some((release) => release.revision === awaitingPublication)) {
      setAwaitingPublication(undefined)
      void refetchChanges()
    }
    if (awaitingCurrent && list.data?.current === awaitingCurrent) {
      setAwaitingCurrent(undefined)
      void refetchChanges()
    }
  }, [awaitingCurrent, awaitingPublication, list.data, refetchChanges])

  const startPublish = async () => {
    promoting.reset()
    setLastOperation(undefined)
    try {
      const accepted = await publish({
        tenant,
        website: site,
        publishWebsite: { operationId: crypto.randomUUID() },
      }).unwrap()
      setLastOperation(accepted)
      if (accepted.status === 'published' && accepted.publication) setAwaitingPublication(accepted.publication)
      else if (accepted.status !== 'failed') setActiveOperation(accepted.id)
    } catch {
      // The mutation exposes the RFC 9457 response below.
    }
  }

  const confirmRollback = async () => {
    if (!rollback) return
    publishing.reset()
    const target = rollback.revision
    setRollback(undefined)
    try {
      await promote({ tenant, website: site, promotePublication: { publication: target } }).unwrap()
      setAwaitingCurrent(target)
    } catch {
      // The mutation exposes the RFC 9457 response below.
    }
  }

  const busy = publishing.isLoading || promoting.isLoading || Boolean(activeOperation || awaitingPublication || awaitingCurrent)
  const status = polled ?? lastOperation
  const mutationError = publishing.error ?? promoting.error

  if (list.isLoading && !list.data) return <Loading label="Reading releases…" />
  if (list.error) return <QueryError error={list.error} title="Could not read releases" />

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
          <Typography variant="h2">Releases</Typography>
          <Typography variant="body2" color="text.secondary">
            Publishing takes a snapshot of the whole site and makes it live. Until you publish, your edits are only
            visible here.
          </Typography>
          {Boolean(list.data?.current) && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
              <Chip size="small" color="success" label={`Live · release ${list.data?.current}`} />
              {list.data?.head !== list.data?.current && (
                <Chip size="small" variant="outlined" label={`Newest · release ${list.data?.head}`} />
              )}
            </Stack>
          )}
        </Stack>
        <Button
          variant="contained"
          startIcon={<PublishIcon />}
          disabled={busy || changes.isLoading || changes.data?.changed === false}
          onClick={() => startPublish().catch(shown)}
        >
          {!list.data?.current || !changes.data
            ? 'Publish site'
            : changes.data.changed
              ? 'Publish changes'
              : 'Published'}
        </Button>
      </Stack>

      {changes.error && <Alert severity="error">{problemMessage(changes.error, 'Could not check for unpublished changes.')}</Alert>}
      {mutationError && <Alert severity="error">{problemMessage(mutationError, 'That did not go through. Nothing has changed.')}</Alert>}
      {operation.error && <Alert severity="error">{problemMessage(operation.error, 'Could not check on the publish in progress.')}</Alert>}
      {status?.status === 'failed' && <Alert severity="error">{status.error || 'Publishing failed. The live site is unchanged.'}</Alert>}
      {(activeOperation || awaitingPublication) && (
        <Alert severity="info">
          {activeOperation ? 'Publishing… taking a snapshot of the site.' : `Release ${awaitingPublication} is going live…`}
        </Alert>
      )}
      {awaitingCurrent && <Alert severity="info">Making release {awaitingCurrent} live…</Alert>}

      {!list.data?.current ? (
        <Alert severity="warning">
          This site has not been published yet. Visitors will see nothing at its address until the first release goes live.
        </Alert>
      ) : !changes.data ? null : changes.data.changed ? (
        <Alert severity="warning">
          {changes.data.changes.length} change{changes.data.changes.length === 1 ? ' is' : 's are'} waiting to go live.
          Visitors still see release {list.data.current}.
        </Alert>
      ) : (
        // Said only on an answer: a changes read that failed, or has not
        // come back, is not evidence that nothing is waiting.
        <Alert severity="success">Everything is live. There are no unpublished changes.</Alert>
      )}

      {changes.data?.changed && (
        <Paper sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="h3">Unpublished changes</Typography>
            {changes.data.changes.map((change) => (
              <Stack
                key={change.id}
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                useFlexGap
              >
                <ComponentName
                  tenant={tenant}
                  id={change.id}
                  revision={change.currentRevision}
                  manifest={manifest}
                />
                <Typography variant="caption" color="text.secondary">
                  {change.publishedRevision
                    ? 'Changed since the last release'
                    : 'New since the last release'}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Release</TableCell>
              <TableCell>Published</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(list.data?.publications ?? []).map((release) => (
              <TableRow key={release.revision}>
                <TableCell>Release {release.revision}</TableCell>
                <TableCell>{new Date(release.publishedAt).toLocaleString()}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {release.current && <Chip size="small" color="success" label="Live" />}
                    {release.revision === list.data?.head && <Chip size="small" variant="outlined" label="Newest" />}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Button size="small" color="inherit" onClick={() => setOpened(release.revision)}>
                    What is in it
                  </Button>
                  {!release.current && (
                    <Button
                      size="small"
                      color="inherit"
                      startIcon={<RestoreIcon />}
                      disabled={busy}
                      onClick={() => setRollback(release)}
                    >
                      {release.revision < (list.data?.current ?? 0) ? 'Roll back' : 'Restore'}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {(list.data?.publications.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color="text.secondary" variant="body2">Nothing has been published yet.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ReleaseContents
        tenant={tenant}
        site={site}
        release={opened}
        manifest={manifest}
        onClose={() => setOpened(undefined)}
      />

      <Dialog open={Boolean(rollback)} onClose={() => !promoting.isLoading && setRollback(undefined)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {rollback && rollback.revision < (list.data?.current ?? 0) ? 'Roll back the site?' : 'Restore this release?'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Release {rollback?.revision} will go live again. Your edits and the newer releases are kept, so you can
            return to them at any time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollback(undefined)} disabled={promoting.isLoading}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={() => confirmRollback().catch(shown)} disabled={promoting.isLoading}>
            Make it live
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
